<template>
	<div class="sections-wrapper">
		<div class="tables-scroll-wrapper">
			<div
				v-for="(section, sectionIndex) in groupedSectionsData"
				:key="sectionIndex"
				class="section-container"
			>
				<div
					class="expandable-header"
					:class="{
						expanded: expandedSections[sectionIndex],
						collapsed: !expandedSections[sectionIndex]
					}"
					@click="toggleExpand(sectionIndex)"
				>
					<i class="fas fa-chevron-right arrow"></i>
					<span>{{ section.sectionName }}</span>
				</div>

				<div
					v-if="expandedSections[sectionIndex]"
					class="table-responsive"
				>
					<table class="table custom-table mt-2">
						<thead>
							<tr>
								<th @click="sortTable(sectionIndex, 'laborOp')">
									Labor Code <i class="fas fa-sort"></i>
								</th>
								<th
									@click="
										sortTable(
											sectionIndex,
											'laborOpDescription'
										)
									"
								>
									Description <i class="fas fa-sort"></i>
								</th>
								<th
									data-testid="header-notes"
									@click="
										sortTable(sectionIndex, 'laborNotes')
									"
								>
									Notes <i class="fas fa-sort"></i>
								</th>
								<th
									@click="
										sortTable(
											sectionIndex,
											'laborAllowanceHours'
										)
									"
								>
									Time <i class="fas fa-sort"></i>
								</th>
								<th colspan="2">Code Details</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, rowIndex) in sortedTableData[
									sectionIndex
								]"
								:key="rowIndex"
							>
								<td>{{ row.laborOp }}</td>
								<td>{{ row.laborOpDescription }}</td>
								<td>{{ row.laborNotes }}</td>
								<td>{{ row.laborAllowanceHours }}</td>
								<td>
									<PopoverContainer
										:visible="
											defectCodesVisibleRecordId ===
												row.id
										"
										triggerEventName="click"
										popoverStrategy="fixed"
									>
										<template #btnSlot>
											<span
												class="link-like-text"
												@click="
													onViewDefectCodes(row.id)
												"
												>View</span
											>
										</template>
										<template #popoverContainerTitle
											>Code Details</template
										>
										<template #popoverContainerBody>
											<div
												class="popover-content-wrapper"
											>
												<div
													v-if="
														row.codeDetails
															.includes &&
															row.codeDetails
																.includes
																.length > 0
													"
												>
													<h4>Includes</h4>
													<ul>
														<li
															v-for="(include,
															index) in row
																.codeDetails
																.includes"
															:key="index"
														>
															{{
																include.relatedLaborOp
															}}
															-
															{{
																include.relatedLaborOpDescription
															}}
														</li>
													</ul>
												</div>
												<div
													v-if="
														row.codeDetails
															.previous &&
															row.codeDetails
																.previous
																.length > 0
													"
												>
													<h4>Previous</h4>
													<ul>
														<li
															v-for="(prev,
															index) in row
																.codeDetails
																.previous"
															:key="index"
														>
															{{
																prev.relatedLaborOp
															}}
															-
															{{
																prev.relatedLaborOpDescription
															}}
														</li>
													</ul>
												</div>
												<div
													v-if="
														row.codeDetails
															.without &&
															row.codeDetails
																.without
																.length > 0
													"
												>
													<h4>Without</h4>
													<ul>
														<li
															v-for="(without,
															index) in row
																.codeDetails
																.without"
															:key="index"
														>
															{{
																without.relatedLaborOp
															}}
															-
															{{
																without.relatedLaborOpDescription
															}}
														</li>
													</ul>
												</div>
											</div>
										</template>
									</PopoverContainer>
								</td>
								<td data-container="body">
									<TooltipContainer
										iconClassName="icon-add_filled"
										:class="getDisabledClass(row.id)"
										tooltip="This labor code has already been selected for import."
										placement="top"
										:tooltipEnabled="isCodeSelected(row.id)"
										@click.native.stop="handleAddCode(row)"
									/>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PopoverContainer from '@/components/shells/popover/popover-container.vue';
import TooltipContainer from '@/components/shells/tooltip/tooltip-container.vue';
import { mapGetters } from 'vuex';

export default {
	components: {
		PopoverContainer,
		TooltipContainer
	},
	props: {
		sectionsData: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			expandedSections: [],
			sortOrders: [],
			sortColumns: [],
			defectCodesVisibleRecordId: 0
		};
	},
	watch: {
		groupedSectionsData: {
			handler(newValue) {
				if (newValue.length) {
					this.expandedSections = newValue.map(() => true);
				}
			},
			immediate: true,
			deep: true
		}
	},
	computed: {
		groupedSectionsData() {
			return this.sectionsData.reduce((acc, item) => {
				const existing = acc.find(
					s => s.sectionName === item.sectionName
				);
				if (existing) {
					existing.items.push(item);
				} else {
					acc.push({
						sectionName: item.sectionName,
						items: [item]
					});
				}
				return acc;
			}, []);
		},
		sortedTableData() {
			return this.groupedSectionsData.map((section, sectionIndex) => {
				if (!this.sortColumns[sectionIndex]) {
					return section.items;
				}

				const column = this.sortColumns[sectionIndex];
				const order = this.sortOrders[sectionIndex];

				return [...section.items].sort((a, b) => {
					const valA = a[column];
					const valB = b[column];

					if (typeof valA === 'number' && typeof valB === 'number') {
						return order === 'asc' ? valA - valB : valB - valA;
					}

					if (typeof valA === 'string' && typeof valB === 'string') {
						return order === 'asc'
							? valA.localeCompare(valB)
							: valB.localeCompare(valA);
					}
					return 0;
				});
			});
		},
		...mapGetters('vwFlatRate', ['selectedLaborCodes'])
	},
	methods: {
		toggleExpand(index) {
			this.$set(
				this.expandedSections,
				index,
				!this.expandedSections[index]
			);
		},
		sortTable(sectionIndex, column) {
			if (this.sortColumns[sectionIndex] === column) {
				this.sortOrders[sectionIndex] =
					this.sortOrders[sectionIndex] === 'asc' ? 'desc' : 'asc';
			} else {
				this.$set(this.sortColumns, sectionIndex, column);
				this.$set(this.sortOrders, sectionIndex, 'asc');
			}
		},
		onViewDefectCodes(laborCodeId) {
			this.defectCodesVisibleRecordId = laborCodeId;
		},
		onAddLaborCode(laborRateRecord) {
			this.$emit('addLaborCode', laborRateRecord);
		},
		isCodeSelected(laborCodeId) {
			return this.selectedLaborCodes.some(c => c.id === laborCodeId);
		},
		handleAddCode(row) {
			if (!this.isCodeSelected(row.id)) {
				this.onAddLaborCode(row);
			}
		},
		getDisabledClass(laborCodeId) {
			return {
				'disabled-icon': this.isCodeSelected(laborCodeId)
			};
		}
	}
};
</script>

<style scoped>
.sections-wrapper {
	display: flex;
	flex-direction: column;
	gap: 15px;
}

.section-container {
	background-color: #f9f9f9;
	border: 1px solid #d1d1d1;
	border-radius: 4px;
	overflow: hidden;
}

.expandable-header {
	cursor: pointer;
	background-color: #f8f9fa;
	padding: 10px 15px;
	border-bottom: 1px solid #d1d1d1;
	display: flex;
	align-items: center;
	font-weight: bold;
	color: #333;
}

.expandable-header .arrow {
	margin-right: 10px;
	transition: transform 0.3s ease;
}

.expandable-header.collapsed .arrow {
	transform: rotate(0deg);
}

.expandable-header.expanded .arrow {
	transform: rotate(90deg);
}

.custom-table {
	width: 100%;
	border-spacing: 0;
	border-collapse: separate;
}

.custom-table th {
	background-color: #e9ecef;
	padding: 10px;
	text-align: left;
	border-bottom: 2px solid #d1d1d1;
	cursor: pointer;
	color: #333;
}

.custom-table th:hover {
	background-color: #dfe3e8;
}

.custom-table td {
	padding: 10px;
	border-bottom: 1px solid #e6e6e6;
}

.custom-table tr:hover {
	background-color: #f9f9f9;
}

.tables-scroll-wrapper {
	max-height: 350px;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 10px;
	border: 1px solid #d1d1d1;
	border-radius: 4px;
	background-color: #ffffff;
}

:deep(.disabled-icon .icon) {
	opacity: 0.5;
}

.popover-content-wrapper {
	max-height: 300px;
	overflow-y: auto;
	padding: 10px;
	border: 1px solid #d1d1d1;
	border-radius: 4px;
	background-color: #ffffff;
}

.popover-content-wrapper h4 {
	margin-top: 15px;
	margin-bottom: 5px;
	font-size: 14px;
	color: #333;
}

.popover-content-wrapper ul {
	margin: 0;
	padding-left: 20px;
}

.popover-content-wrapper ul li {
	margin-bottom: 5px;
	font-size: 13px;
	color: #555;
}
.link-like-text {
	color: #007bff;
	text-decoration: none;
	cursor: pointer;
}

.link-like-text:hover {
	text-decoration: underline;
}
</style>
